import {useCallback, type RefObject} from "react";
import {useIsomorphicLayoutEffect} from "utils/useIsomorphicLayoutEffect";
import {campaignSpace, campaignReserve, clearsCornerControl, cornerInset} from "./campaignSpace";
import {topbarBandBottom} from "nav/topbarBand";
import {readWithdrawalControlBox} from "consent/withdrawalControl";

/**
 * Measure the real card and the consent banner's existing safe-area value before painting the promotion, and on the
 * download page the consent-withdrawal control's corner too.
 */
export function useCampaignSpace(reading: RefObject<HTMLDivElement | null>, assigned: boolean) {
	const measure = useCallback(() => {
		const node = reading.current;
		const surface = node?.closest<HTMLElement>("[data-campaign-surface]");
		const card = surface?.querySelector<HTMLElement>("[data-peel-frame], [data-create-card], [data-testid='recipient-card']");
		if (!assigned || !node || !surface || !card) return;
		const header = document.querySelector<HTMLElement>("header");
		// Hidden while its panel is inert (a gate or a sheet over the card), and a hidden tab occupies nothing.
		const tab = card.querySelector<HTMLElement>("[data-request-tab]");
		const tabShown = tab !== null && getComputedStyle(tab).visibility !== "hidden";
		const flow = card.closest<HTMLElement>("[data-drop-container]");
		const hero = flow?.closest("section");
		const slot = node.parentElement;
		if (flow && hero && slot && window.innerWidth <= 900) {
			// The SURFACE is the box this stack is laid out in, and it does not always end at the window:
			// ui#2394's legal footer row sits below it on every route `nav/siteFooterRoutes` keeps the row
			// on. The reservation's room is read from the surface rather than from `window.innerHeight`;
			// where a surface does reach the window's edge — the landing since ui#2681, and the delivery
			// pages always — the two are the same number and this reading needs no special case.
			const padding = getComputedStyle(hero);
			// The DESIGNED gap, published statically by `LandingCampaign.styles`, never the gap currently
			// applied: the applied one is this reading's own output, so feeding it back in would let the
			// two chase each other frame after frame.
			const designGap = parseFloat(getComputedStyle(slot).getPropertyValue("--campaign-slot-design-gap")) || 0;
			const reserve = campaignReserve({
				availableHeight: surface.clientHeight,
				heroPadding: parseFloat(padding.paddingTop) + parseFloat(padding.paddingBottom),
				flowHeight: flow.getBoundingClientRect().height,
				designGap,
			});
			for (const [name, measurement] of [["--campaign-reserve-cap", reserve.height], ["--campaign-slot-gap", reserve.gap]] as const) {
				const value = `${measurement}px`;
				if (slot.style.getPropertyValue(name) !== value) slot.style.setProperty(name, value);
			}
		}
		const area = campaignSpace({
			viewportWidth: window.innerWidth, viewportHeight: window.innerHeight,
			card: card.getBoundingClientRect(), headerBottom: topbarBandBottom(header),
			consentReserve: Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--consent-bar-reserve")) || 0,
			// A live rect, like the card's above and for the same reason: both are flex children of the app
			// scroller, so both move when it scrolls, and the promotion is placed against where they ARE.
			// That is what makes the scroll subscription below part of the reading rather than a nicety.
			surfaceBottom: surface.getBoundingClientRect().bottom,
			tabTop: tabShown ? tab.getBoundingClientRect().top : null,
		});
		if (area === null) {
			node.removeAttribute("data-mobile-space");
			node.removeAttribute("data-space-strip");
			node.style.removeProperty("--campaign-corner-inset");
			// Measured, and nowhere on screen to put it: the landing's stacked layout folds the reading away
			// rather than draw it past the room it was given (CampaignSurface.styles).
			node.setAttribute("data-no-space", "");
			return;
		}
		node.removeAttribute("data-no-space");
		for (const key of ["left", "top", "width", "height"] as const) {
			const name = `--campaign-space-${key}`, value = `${area[key]}px`;
			if (node.style.getPropertyValue(name) !== value) node.style.setProperty(name, value);
		}
		node.setAttribute("data-mobile-space", "");
		node.toggleAttribute("data-space-strip", area.strip);
		// Placed, so the copy's rows are where they will paint: on the download page a copy reaching the
		// consent-withdrawal control's band moves right of it (kitecorp/transferify#536), and the landing is left as it
		// is. The active copy, the one the dissolve is not fading out.
		const copy = clearsCornerControl(surface.dataset.campaignSurface)
			? node.querySelector<HTMLElement>(":scope > [data-edition]:not([aria-hidden])")
			: null;
		if (copy === null) {
			node.style.removeProperty("--campaign-corner-inset");
		} else {
			const {top, bottom} = copy.getBoundingClientRect();
			const rootStyle = getComputedStyle(document.documentElement);
			const inset = `${cornerInset({top, bottom, left: area.left}, readWithdrawalControlBox(name => rootStyle.getPropertyValue(name)))}px`;
			if (node.style.getPropertyValue("--campaign-corner-inset") !== inset) node.style.setProperty("--campaign-corner-inset", inset);
		}
	}, [reading, assigned]);

	useIsomorphicLayoutEffect(measure);
	useIsomorphicLayoutEffect(() => {
		const resize = new ResizeObserver(measure);
		let observedCard: Element | null = null;
		const syncCard = () => {
			const card = reading.current?.closest("[data-campaign-surface]")?.querySelector("[data-peel-frame], [data-create-card], [data-testid='recipient-card']") ?? null;
			if (card !== observedCard) {
				if (observedCard) resize.unobserve(observedCard);
				if (card) resize.observe(card);
				observedCard = card;
			}
			measure();
		};
		syncCard();
		// The banner publishes its reserve on the root and the withdrawal control its box; observing the root's style
		// avoids a second banner-height rule, and catches the control arriving once the banner is answered.
		const root = new MutationObserver(measure);
		root.observe(document.documentElement, {attributes: true, attributeFilter: ["style", "data-theme"]});
		const content = new MutationObserver(syncCard);
		// `inert` too: the card's tab is shown or hidden with it (a gate or a sheet over the card), and nothing
		// else changes size when it flips.
		content.observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ["inert"]});
		window.addEventListener("resize", measure);
		/*
		 * SCROLL is a reading of its own, because both boxes this allocation is built from — the card and
		 * the surface's end — are flex children of the app scroller and move when it scrolls, while the
		 * promotion itself is fixed and does not (ui#2645).
		 *
		 * Capture phase: the DOCUMENT never scrolls here, `[data-app-scroller]` does, and a scroll event
		 * does not bubble to the window from an element. Same reason `home/HomePage` listens this way.
		 *
		 * Without it the fix for ui#2645 would have held only by accident: at `scrollTop` 0 with the
		 * undecided banner the two-row footer is at 453-568, so the floor computes to the same 364 master
		 * used, and the overlap appears only once a wheel has lifted the footer to 259. A re-render or a
		 * slide change happened to re-measure — neither of which a `prefers-reduced-motion` visitor gets,
		 * because `campaignPlayback` pauses on `reduced`.
		 *
		 * Coalesced to one measurement per frame for the reason `consent/CookieBanner` gives: every
		 * measurement forces a layout and writes custom properties, and a scroll fires per frame already.
		 */
		let frame: number | null = null;
		const scheduleMeasure = () => {
			if (frame !== null) return;
			frame = window.requestAnimationFrame(() => {
				frame = null;
				measure();
			});
		};
		window.addEventListener("scroll", scheduleMeasure, true);
		return () => {
			resize.disconnect(); root.disconnect(); content.disconnect();
			window.removeEventListener("resize", measure);
			window.removeEventListener("scroll", scheduleMeasure, true);
			if (frame !== null) window.cancelAnimationFrame(frame);
		};
	}, [measure]);
}
